import React, { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Trash2, Search, Mail, Users } from "lucide-react";

export default function NewsletterAdminTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/newsletter/subscribers");
      setSubs(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await api.delete(`/admin/newsletter/subscribers/${id}`);
      toast.success("Subscriber removed.");
      setSubs((prev) => prev.filter((s) => s.subscriber_id !== id));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to remove.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/newsletter/export`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drthrive365-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (err) {
      toast.error("Failed to export CSV.");
    }
  };

  const filtered = subs.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      (s.name || "").toLowerCase().includes(needle) ||
      (s.email || "").toLowerCase().includes(needle) ||
      (s.phone || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-white/85">
            <Users size={18} className="text-[#FF8A00]" />
            <span className="font-display text-xl">Newsletter subscribers</span>
          </div>
          <p className="text-sm text-white/50 mt-1">
            {subs.length} total {subs.length === 1 ? "subscriber" : "subscribers"} captured from the landing page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="subscribers-search"
              placeholder="Search name, email, phone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9 w-64 bg-black/40 border-white/10"
            />
          </div>
          <Button
            onClick={exportCsv}
            data-testid="export-subscribers-csv"
            className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 h-9"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-[11px] tracking-[0.15em] uppercase">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Consent</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="subscribers-table-body">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/50">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-white/50">
                    <Mail className="mx-auto mb-3 text-white/30" size={24} />
                    {q ? "No matches for that search." : "No subscribers yet. Share your landing page!"}
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.subscriber_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">{s.name}</td>
                    <td className="px-4 py-3 text-white/80">{s.email}</td>
                    <td className="px-4 py-3 text-white/70">{s.phone}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full border tracking-wider uppercase ${
                            s.consent_email
                              ? "bg-[#FF8A00]/10 text-[#FF8A00] border-[#FF8A00]/30"
                              : "bg-white/5 text-white/35 border-white/10"
                          }`}
                          title={s.consented_at ? `Consented ${new Date(s.consented_at).toLocaleString()}` : ""}
                        >
                          {s.consent_email ? "Email ✓" : "Email —"}
                        </span>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full border tracking-wider uppercase ${
                            s.consent_sms
                              ? "bg-[#E52E71]/10 text-[#E52E71] border-[#E52E71]/30"
                              : "bg-white/5 text-white/35 border-white/10"
                          }`}
                          title={s.consented_at ? `Consented ${new Date(s.consented_at).toLocaleString()}` : ""}
                        >
                          {s.consent_sms ? "SMS ✓" : "SMS —"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/50">
                      {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(s.subscriber_id, s.email)}
                        data-testid={`delete-subscriber-${s.subscriber_id}`}
                        className="p-2 rounded-md hover:bg-red-500/10 text-red-400/80 hover:text-red-300 transition"
                        title="Remove subscriber"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
